import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Upload, Link as LinkIcon, X, RotateCw, ZoomIn, Loader2 } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { useRbac } from '../context';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Area } from 'react-easy-crop/types';
import { uploadFile } from '../services/usersapi';
import { updateMyProfile } from '../services/usersapi';
import { getCurrentUserDetails } from '../services/apiclient';
import { toast } from 'react-hot-toast';

const PROFILE_COMPLETION_POPUP_KEY = 'profileCompletionPopupLastShown';
const POPUP_INTERVAL_MS = 2 * 60 * 60 * 500; // 1 hours in milliseconds
// const POPUP_INTERVAL_MS =10; // 2 hours in milliseconds



/**
 * ProfileCompletionPopup Component
 * Shows a popup reminder when linkedinUrl and/or profilePhotoUrl are missing
 * Appears every 2 hours if the fields are still missing
 */

// Utility function to create cropped image
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to match the crop area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      const url = URL.createObjectURL(blob);
      resolve(url);
    }, 'image/jpeg', 0.95);
  });
};

// Component to render form fields based on what's missing from backend
const ProfileUpdateContent: React.FC<{
  user: any;
  linkedinUrl: string;
  handleLinkedInUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  profilePhotoPreview: string | null;
  handleProfilePhotoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ user, linkedinUrl, handleLinkedInUrlChange, profilePhotoPreview, handleProfilePhotoChange }) => {
  const [userData, setUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch latest user data from backend
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getCurrentUserDetails();
        setUserData(response?.data?.user);
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fallback to user from context if API fails
        setUserData(user);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
      </div>
    );
  }

  const currentUser = userData || user;
  const missingLinkedIn = !currentUser?.linkedinUrl || currentUser?.linkedinUrl === null || currentUser?.linkedinUrl === '';
  const missingProfilePhoto = !currentUser?.profilePhotoUrl || currentUser?.profilePhotoUrl === null || currentUser?.profilePhotoUrl === '';

  // Dynamic message based on what's missing
  let message = '';
  if (missingLinkedIn && missingProfilePhoto) {
    console.log('missingLinkedIn and missingProfilePhoto', missingLinkedIn, missingProfilePhoto, currentUser);
    message = 'Linkedin Profile URL and Profile Photo not updated in your profile';
  } else if (missingLinkedIn) {
    message = 'Linkedin Profile URL not updated in your profile';
  } else if (missingProfilePhoto) {
    message = 'Profile Photo not updated in your profile';
  }

  return (
    <div className="flex-1 space-y-6">
      {message && (
        <p className="text-base text-gray-700 leading-relaxed">{message}</p>
      )}

      {/* LinkedIn URL Input - Only show if missing */}
      {missingLinkedIn && (
        <div className="space-y-2">
          <Label htmlFor="linkedin-url" className="text-sm font-medium text-gray-700">
            LinkedIn URL
          </Label>
          <div className="relative">
            <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              id="linkedin-url"
              type="url"
              placeholder="https://www.linkedin.com/in/yourprofile"
              value={linkedinUrl}
              onChange={handleLinkedInUrlChange}
              className="pl-10"
            />
          </div>
        </div>
      )}

      {/* Profile Photo Upload - Only show if missing */}
      {missingProfilePhoto && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Profile Photo
          </Label>
          <Card className="border-2 border-dashed hover:border-orange-500 transition-colors cursor-pointer">
            <CardContent className="p-4">
              <div
                className="flex flex-col items-center justify-center space-y-2"
                onClick={() => {
                  document.getElementById('profile-photo-upload-popup')?.click();
                }}
              >
                {profilePhotoPreview ? (
                  <div className="relative">
                    <img
                      src={profilePhotoPreview}
                      alt="Profile Preview"
                      className="w-24 h-24 object-cover border-2 border-gray-200 rounded-lg cursor-pointer"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 rounded-lg transition-opacity flex items-center justify-center">
                      <Upload className="h-6 w-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                      <Upload className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Click to upload photo</p>
                    <p className="text-xs text-gray-400">JPG, PNG or GIF (max. 5MB)</p>
                  </>
                )}
                <input
                  id="profile-photo-upload-popup"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoChange}
                  className="hidden"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const ProfileCompletionPopup: React.FC = () => {
  const { user, setUser } = useRbac();
  const [isOpen, setIsOpen] = useState(false);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (!user) {
      setIsOpen(false);
      return;
    }

    // Don't show popup for COMPANY role users
    if (user.role === 'COMPANY') {
      setIsOpen(false);
      return;
    }

    // Function to fetch user data from backend and check if popup should show
    const checkAndShowPopup = async (isInitialCheck: boolean = false) => {
      try {
        // Don't auto-close if popup is already open and user is interacting with it
        // Only check to potentially open the popup, not close it
        if (isOpen && !isInitialCheck) {
          return;
        }

        // Fetch latest user data from backend
        const response = await getCurrentUserDetails();
        const currentUserData = response?.data?.user;
        
        if (!currentUserData) {
          if (isInitialCheck) {
            setIsOpen(false);
          }
          return;
        }

        // Don't show popup for COMPANY role users
        if (currentUserData.role === 'COMPANY') {
          if (isInitialCheck) {
            setIsOpen(false);
          }
          return;
        }

        // Check if linkedinUrl or profilePhotoUrl are missing/null/undefined
        const missingLinkedIn = !currentUserData.linkedinUrl || currentUserData.linkedinUrl === null || currentUserData.linkedinUrl === '';
        const missingProfilePhoto = !currentUserData.profilePhotoUrl || currentUserData.profilePhotoUrl === null || currentUserData.profilePhotoUrl === '';
        
        // If both fields are present, don't show popup at all
        if (!missingLinkedIn && !missingProfilePhoto) {
          if (isInitialCheck) {
            setIsOpen(false);
          }
          return;
        }

        // Check if enough time has passed since last popup
        const lastShown = localStorage.getItem(PROFILE_COMPLETION_POPUP_KEY);
        const currentTime = Date.now();
        
        if (!lastShown) {
          // Never shown before, show it now
          setIsOpen(true);
          localStorage.setItem(PROFILE_COMPLETION_POPUP_KEY, currentTime.toString());
          return;
        }

        const lastShownTime = parseInt(lastShown, 10);
        const timeDiff = currentTime - lastShownTime;

        if (timeDiff >= POPUP_INTERVAL_MS) {
          setIsOpen(true);
          localStorage.setItem(PROFILE_COMPLETION_POPUP_KEY, currentTime.toString());
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // On error, don't show popup
        if (isInitialCheck) {
          setIsOpen(false);
        }
      }
    };

    // Initial check
    checkAndShowPopup(true);

    // Set up interval to check every minute (to catch when 2 hours have passed)
    // This will only open the popup, not force-close it if user is actively using it
    const interval = setInterval(() => {
      checkAndShowPopup(false);
    }, 60000); // Check every minute

    return () => {
      clearInterval(interval);
    };
  }, [user, isOpen]);

  // Initialize form values when popup opens
  useEffect(() => {
    if (isOpen && user) {
      // Only initialize fields that are missing (empty strings for inputs)
      // Don't pre-fill existing values since those fields won't be shown
      const missingLinkedIn = !user.linkedinUrl || user.linkedinUrl === null || user.linkedinUrl === '';
      const missingProfilePhoto = !user.profilePhotoUrl || user.profilePhotoUrl === null || user.profilePhotoUrl === '';
      
      // Only set to empty if field is missing (will be shown in UI)
      if (missingLinkedIn) {
        setLinkedinUrl('');
      }
      
      // Only set to null if field is missing (will be shown in UI)
      if (missingProfilePhoto) {
        setProfilePhotoPreview(null);
      }
    }
  }, [isOpen, user]);

  const handleClose = () => {
    setIsOpen(false);
    // Reset form values
    setLinkedinUrl('');
    setProfilePhotoPreview(null);
  };

  const handleLinkedInUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkedinUrl(e.target.value);
  };

  const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        e.target.value = ''; // Reset input
        return;
      }
      
      // Validate file size (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size exceeds 5MB limit');
        e.target.value = ''; // Reset input
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setImageSrc(previewUrl);
      // Reset crop settings
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      // Open crop modal
      setShowCropModal(true);
    }
  };

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoom(parseFloat(e.target.value));
  };

  const handleRotation = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleCropComplete = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      return;
    }

    try {
      const croppedImageUrl = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Convert blob URL to blob for file upload
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      setCroppedImageBlob(blob);
      
      // Create preview URL from blob
      const previewUrl = URL.createObjectURL(blob);
      setProfilePhotoPreview(previewUrl);
      setShowCropModal(false);
      
      // Clean up old URLs
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      toast.error('Failed to crop image. Please try again.');
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    if (imageSrc) {
      URL.revokeObjectURL(imageSrc);
      setImageSrc(null);
    }
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  };

  const handleUpdateProfile = async () => {
    if (!user) return;

    setIsUpdating(true);
    try {
      // First, fetch latest user data from backend to check which fields are missing
      const currentUserResponse = await getCurrentUserDetails();
      const currentUserData = currentUserResponse?.data?.user;
      
      if (!currentUserData) {
        toast.error('Failed to fetch user data');
        setIsUpdating(false);
        return;
      }

      let profilePhotoUrl: string | undefined = undefined;

      // If there's a cropped image blob, upload it first
      if (croppedImageBlob) {
        try {
          // Convert blob to File
          const file = new File([croppedImageBlob], 'profile-photo.jpg', { type: 'image/jpeg' });
          const folderPath = `users/${user.id}/profile`;
          
          const uploadResponse = await uploadFile(file, folderPath);
          profilePhotoUrl = uploadResponse.fileUrl;
          
          // Clean up blob
          setCroppedImageBlob(null);
        } catch (uploadError) {
          console.error('Error uploading profile photo:', uploadError);
          toast.error('Failed to upload profile photo');
          setIsUpdating(false);
          return;
        }
      } else if (profilePhotoPreview && profilePhotoPreview.startsWith('http')) {
        // If profilePhotoPreview is already a URL (not a blob URL), use it directly
        profilePhotoUrl = profilePhotoPreview;
      }

      // Prepare update data - only include fields that are actually missing
      const updateData: {
        linkedinUrl?: string;
        profilePhotoUrl?: string;
      } = {};

      // Check which fields are missing from backend data
      const missingLinkedIn = !currentUserData.linkedinUrl || currentUserData.linkedinUrl === null || currentUserData.linkedinUrl === '';
      const missingProfilePhoto = !currentUserData.profilePhotoUrl || currentUserData.profilePhotoUrl === null || currentUserData.profilePhotoUrl === '';

      // Only update LinkedIn URL if it was missing
      if (missingLinkedIn) {
        if (linkedinUrl !== undefined && linkedinUrl.trim() !== '') {
          updateData.linkedinUrl = linkedinUrl.trim();
        }
      }

      // Only update Profile Photo if it was missing
      if (missingProfilePhoto) {
        if (profilePhotoUrl !== undefined) {
          updateData.profilePhotoUrl = profilePhotoUrl;
        }
      }

      // Only update if there's data to update
      if (Object.keys(updateData).length === 0) {
        toast.error('Please provide at least one field to update');
        setIsUpdating(false);
        return;
      }

      // Update profile
      await updateMyProfile(updateData);

      // Fetch updated user data from backend
      const updatedUserResponse = await getCurrentUserDetails();
      const updatedUserData = updatedUserResponse?.data?.user;

      // Update user context with new data from backend
      if (updatedUserData && setUser) {
        const updatedUser = {
          ...user,
          linkedinUrl: updatedUserData.linkedinUrl,
          profilePhotoUrl: updatedUserData.profilePhotoUrl,
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      // Reset form and close popup
      setLinkedinUrl('');
      setProfilePhotoPreview(null);
      setIsOpen(false);
      
      // Clear the popup timer so it won't show again if fields are now filled
      const updatedLinkedIn = updatedUserData?.linkedinUrl;
      const updatedPhoto = updatedUserData?.profilePhotoUrl;
      if (updatedLinkedIn && updatedPhoto) {
        localStorage.removeItem(PROFILE_COMPLETION_POPUP_KEY);
      }

      // Toast is already shown by updateMyProfile service
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  // Don't render if user doesn't exist or popup shouldn't show
  if (!user || !isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Allow closing
      if (!open) {
        handleClose();
      }
    }}>
      <DialogContent className="sm:max-w-md border-0 outline-none ring-0 focus:ring-0 focus:outline-none">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-orange-600" />
            <DialogTitle className="text-xl font-semibold">
              Profile Update Required
            </DialogTitle>
          </div>
        </DialogHeader>
        
        <ProfileUpdateContent 
          user={user}
          linkedinUrl={linkedinUrl}
          handleLinkedInUrlChange={handleLinkedInUrlChange}
          profilePhotoPreview={profilePhotoPreview}
          handleProfilePhotoChange={handleProfilePhotoChange}
        />

        <div className="flex items-center justify-end gap-2 pt-4 flex-shrink-0 border-t mt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="text-base"
          >
            Close
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700 text-white text-base disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleUpdateProfile}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Profile'
            )}
          </Button>
        </div>
      </DialogContent>

      {/* Crop Modal */}
      <Dialog open={showCropModal} onOpenChange={(open) => !open && handleCropCancel()}>
        <DialogContent className="sm:max-w-2xl max-w-[90vw] border-0 outline-none ring-0 focus:ring-0 focus:outline-none [&>button]:hidden">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-orange-600" />
              <DialogTitle className="text-xl font-semibold">
                Crop Profile Photo (1:1)
              </DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="flex-1 space-y-4">
            {/* Image Container with Crop Area */}
            <div className="relative w-full h-[400px] bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-300">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1} // 1:1 ratio
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  cropShape="rect"
                  showGrid={true}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                    },
                  }}
                />
              )}
            </div>

            {/* Controls */}
            <div className="space-y-4 pt-4 border-t">
              {/* Zoom Control */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <ZoomIn className="h-4 w-4" />
                    Zoom
                  </Label>
                  <span className="text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={handleZoomChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  onClick={handleRotation}
                  className="flex items-center gap-2"
                >
                  <RotateCw className="h-4 w-4" />
                  Rotate
                </Button>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCropCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCropComplete}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    disabled={!croppedAreaPixels}
                  >
                    Apply Crop
                  </Button>
                </div>
              </div>
            </div>
          </div>

        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ProfileCompletionPopup;
